import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { API } from "../../lib/api";
import TermReportsDrawer from "./TermReportsDrawer";
import {
  UserPlus, Search, Download, Cake, X, Check, Wallet,
  CreditCard, MoreVertical, Edit3, Ban, Power, Trash2, FileText,
  IdCard, FileUp, FileDown, Upload, Paperclip, CalendarDays, Receipt, Camera, CheckCircle2, GraduationCap,
} from "lucide-react";

const DOC_CATEGORIES = [
  "Birth Certificate", "Immunization Record", "Medical Report",
  "Aadhaar / ID Proof", "Address Proof", "Passport Photo", "Other",
];

const formatBytes = (b) => {
  if (!b) return "0 B";
  const k = 1024, sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return `${(b / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const CLASSES = ["Toddlers", "Early Years 1", "Early Years 2", "Early Years 3"];
const STATUSES = ["active", "disabled", "withdrawn", "passed_out"];
const ARCHIVE_STATUSES = "withdrawn,disabled,passed_out"; // comma-list for the backend
const PAY_MODES = ["Cash", "Online", "Cheque", "UPI", "Bank Transfer"];
const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

// Generate the academic-year sessions list (current, ±2 years) — "YYYY-YYYY"
function currentSessionLabel() {
  const now = new Date();
  const y = now.getFullYear();
  const start = now.getMonth() >= 3 ? y : y - 1; // April rollover
  return `${start}-${start + 1}`;
}
function sessionOptions() {
  const now = new Date();
  const y = now.getFullYear();
  const cur = now.getMonth() >= 3 ? y : y - 1;
  const list = [];
  for (let i = -3; i <= 2; i += 1) {
    const s = cur + i;
    list.push(`${s}-${s + 1}`);
  }
  return list.reverse(); // newest first
}

function ageString(dob) {
  if (!dob) return "";
  const d = new Date(dob);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  let months = now.getMonth() - d.getMonth();
  let days = now.getDate() - d.getDate();
  if (days < 0) { months -= 1; days += 30; }
  if (months < 0) { years -= 1; months += 12; }
  return `${years} yrs, ${months} mo, ${days} d`;
}

const STATUS_PILL = {
  active: "bg-[#DCEFE1] text-[#1f6e3a] border-[#1f6e3a]/20",
  disabled: "bg-[#FFEFD5] text-[#a06400] border-[#a06400]/20",
  withdrawn: "bg-red-100 text-red-700 border-red-300",
  passed_out: "bg-[#E3EDF9] text-[#0F1B47] border-[#0F1B47]/20",
};

export default function StudentsPanel({ can, mode = "active", openStudentId = null }) {
  const archive = mode === "archive";
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [klass, setKlass] = useState("all");
  const [status, setStatus] = useState(archive ? "all" : "active"); // archive mode hides this filter
  const [session, setSession] = useState(openStudentId ? "all" : currentSessionLabel());
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [ledgerStudent, setLedgerStudent] = useState(null);
  const [docsStudent, setDocsStudent] = useState(null);
  const [termReportsStudent, setTermReportsStudent] = useState(null);
  const [attendStudent, setAttendStudent] = useState(null);
  const [receiptsStudent, setReceiptsStudent] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showPromote, setShowPromote] = useState(false);
  const [deepLinkConsumed, setDeepLinkConsumed] = useState(false);

  const downloadIdCard = async (s) => {
    try {
      const res = await adminApi.get(`/admin/students/${s.id}/id-card.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `naavi-id-${(s.admission_id || s.id).replace(/\//g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "ID card download failed");
    }
  };

  const downloadParentIdCard = async (s) => {
    try {
      const res = await adminApi.get(`/admin/students/${s.id}/parent-id-card.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `naavi-parent-id-${(s.admission_id || s.id).replace(/\//g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Parent ID card download failed");
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const params = {};
      if (klass !== "all") params.klass = klass;
      if (archive) {
        // Archive view: show only withdrawn/disabled/passed-out, regardless of `status` filter
        params.status = ARCHIVE_STATUSES;
      } else if (status !== "all") {
        params.status = status;
      } else {
        // Active view + "all statuses" — explicitly exclude archived rows
        params.status = "active";
      }
      if (session && session !== "all") params.session = session;
      const res = await adminApi.get("/admin/students", { params });
      setStudents(res.data);
    } catch (e) {
      // 401 handled globally
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [klass, status, session, archive]); // eslint-disable-line

  // Auto-open the student edit form when a deep-link student id is provided (QR scan flow)
  useEffect(() => {
    if (!openStudentId || deepLinkConsumed || loading) return;
    const found = students.find((s) => s.id === openStudentId);
    if (found) {
      setEditing(found);
      setShowForm(true);
      setDeepLinkConsumed(true);
    }
  }, [openStudentId, deepLinkConsumed, loading, students]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return students;
    return students.filter((s) => {
      const hay = [
        s.full_name, s.admission_id,
        s.father?.name, s.father?.phone,
        s.mother?.name, s.mother?.phone,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(ql);
    });
  }, [students, q]);

  const exportCSV = async () => {
    try {
      const res = await adminApi.get("/admin/students.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "naavi-students.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  const exportBirthdays = () => {
    const month = new Date().getMonth();
    const rows = students.filter((s) => {
      if (!s.dob) return false;
      const d = new Date(s.dob);
      return !isNaN(d.getTime()) && d.getMonth() === month;
    });
    if (rows.length === 0) {
      toast.info("No birthdays this month.");
      return;
    }
    const csv = [
      ["Admission ID", "Name", "Class", "DOB", "Parent", "Phone"].join(","),
      ...rows.map((s) => [
        s.admission_id, `"${s.full_name}"`, s.class, s.dob,
        `"${s.father?.name || s.mother?.name || ""}"`,
        s.father?.phone || s.mother?.phone || "",
      ].join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `naavi-birthdays-${new Date().toLocaleString("default", { month: "short" }).toLowerCase()}.csv`;
    a.click();
  };

  const toggleStatus = async (s, next) => {
    try {
      await adminApi.patch(`/admin/students/${s.id}`, { status: next });
      toast.success(`Marked ${next}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const removeStudent = async (s) => {
    if (!window.confirm(`Permanently remove ${s.full_name}? Their ledger will also be deleted.`)) return;
    try {
      await adminApi.delete(`/admin/students/${s.id}`);
      toast.success("Student removed.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="bg-[#FDFDF9] rounded-3xl border border-[#0F1B47]/8 overflow-hidden" data-testid="students-panel">
      <div className="p-5 md:p-7 border-b border-[#0F1B47]/8 flex flex-col gap-4">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div>
            <h2 className="font-display text-2xl text-[#0F1B47] font-medium">{archive ? "Archive" : "Students"}</h2>
            <p className="text-sm text-[#4A5568] mt-1">
              {archive
                ? "Students who have been withdrawn, disabled, or passed out. Restore them anytime from the actions menu."
                : "Enrolled children across Toddlers and Early Years."}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {can("export_csv") && !archive && (
              <>
                <button
                  onClick={exportCSV}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10"
                  data-testid="students-export-csv"
                >
                  <Download size={14} /> Excel / CSV
                </button>
                <button
                  onClick={exportBirthdays}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#FFE9C7] hover:bg-[#FFD89A]"
                  data-testid="students-birthdays"
                >
                  <Cake size={14} /> Birthdays this month
                </button>
              </>
            )}
            {can("edit_students") && !archive && (
              <>
                <button
                  onClick={() => setShowPromote(true)}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#C6D947] hover:bg-[#d6e860]"
                  data-testid="students-promote-btn"
                  title="Move students up to the next academic year"
                >
                  <GraduationCap size={14} /> Promote to next year
                </button>
                <button
                  onClick={() => { setEditing(null); setShowForm(true); }}
                  className="inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b]"
                  data-testid="students-add-btn"
                >
                  <UserPlus size={15} /> Add student
                </button>
              </>
            )}
          </div>
        </div>

        {/* Filter bar — Search · Session · Class · Status (Hello Parent reference) */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-4">
            <span className="text-[11px] font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-1.5 block">Search student</span>
            <div className="relative">
              <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#718096]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Admission ID / Name / Father / Mobile"
                className="w-full bg-[#F5F5F0] border border-[#0F1B47]/10 rounded-2xl pl-11 pr-4 py-2.5 text-[14px] focus:outline-none focus:border-[#648DE5] focus:bg-[#FDFDF9]"
                data-testid="students-search"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <span className="text-[11px] font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-1.5 block">Session</span>
            <select
              value={session}
              onChange={(e) => setSession(e.target.value)}
              className="w-full bg-[#F5F5F0] border border-[#0F1B47]/10 rounded-2xl px-4 py-2.5 text-[14px] focus:outline-none focus:border-[#648DE5] focus:bg-[#FDFDF9]"
              data-testid="students-filter-session"
            >
              <option value="all">All sessions</option>
              {sessionOptions().map((s) => <option key={s} value={s}>{s.replace("-", "–")}</option>)}
            </select>
          </div>
          <div className="md:col-span-3">
            <span className="text-[11px] font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-1.5 block">Class</span>
            <select
              value={klass}
              onChange={(e) => setKlass(e.target.value)}
              className="w-full bg-[#F5F5F0] border border-[#0F1B47]/10 rounded-2xl px-4 py-2.5 text-[14px] focus:outline-none focus:border-[#648DE5] focus:bg-[#FDFDF9]"
              data-testid="students-filter-class"
            >
              <option value="all">All classes</option>
              {CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {!archive ? (
            <div className="md:col-span-2 text-right">
              <span className="text-[11px] font-semibold text-[#718096]" data-testid="students-count">{filtered.length} record{filtered.length === 1 ? "" : "s"}</span>
            </div>
          ) : (
            <div className="md:col-span-2 text-right">
              <span className="text-[11px] font-semibold text-[#718096]" data-testid="students-count">{filtered.length} archived</span>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-[#718096] text-sm">Loading students…</div>
      ) : filtered.length === 0 ? (
        <div className="p-12 text-center">
          <div className="text-[#0F1B47] font-medium mb-1">{archive ? "Archive is empty" : "No students yet"}</div>
          <p className="text-sm text-[#718096]">
            {archive
              ? "Students will appear here once you mark them withdrawn, disabled, or passed out."
              : can("edit_students")
                ? "Click \"Add student\" to enroll the first child."
                : "Ask your superadmin to grant edit_students permission."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="students-table">
            <thead>
              <tr className="bg-[#0F1B47]/5 text-[10px] font-bold tracking-[0.16em] uppercase text-[#0F1B47]">
                <th className="px-4 py-3 text-left w-20"> </th>
                <th className="px-4 py-3 text-left">Admission ID</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Class</th>
                <th className="px-4 py-3 text-left">Parent details</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody data-testid="students-list">
              {filtered.map((s) => (
                <tr key={s.id} className="border-t border-[#0F1B47]/8 hover:bg-[#F5F5F0]/60 transition-colors align-top" data-testid={`student-row-${s.id}`}>
                  <td className="px-4 py-4">
                    <Avatar url={s.photo_url} name={s.full_name} />
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-[12px] font-mono text-[#0F1B47] tracking-wide">{s.admission_id}</div>
                    <div className="mt-1.5 flex flex-col gap-1">
                      <span className={`text-[9px] font-bold tracking-[0.16em] uppercase px-2 py-0.5 rounded-full border w-fit ${STATUS_PILL[s.status] || STATUS_PILL.active}`}>
                        {s.status || "active"}
                      </span>
                      {s.balance > 0 && (
                        <span className="text-[9px] font-bold tracking-[0.16em] uppercase px-2 py-0.5 rounded-full bg-red-100 text-red-700 w-fit">
                          Due {INR(s.balance)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 min-w-[180px]">
                    <div className="font-semibold text-[#0F1B47] text-[14px]">{s.full_name}</div>
                    {s.dob && <div className="text-[11px] text-[#718096] mt-1">{ageString(s.dob)}</div>}
                  </td>
                  <td className="px-4 py-4 text-[#0F1B47] text-[13px] whitespace-nowrap">{s.class}</td>
                  <td className="px-4 py-4 min-w-[220px] text-[12px] text-[#4A5568]">
                    {s.father?.name && (
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 size={11} className="text-emerald-600 shrink-0" />
                        <span className="text-[#0F1B47] font-medium">{s.father.name}</span>
                        {s.father.phone && <span className="text-[#4A5568]">: {s.father.phone}</span>}
                      </div>
                    )}
                    {s.mother?.name && (
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <CheckCircle2 size={11} className="text-emerald-600 shrink-0" />
                        <span className="text-[#0F1B47] font-medium">{s.mother.name}</span>
                        {s.mother.phone && <span className="text-[#4A5568]">: {s.mother.phone}</span>}
                      </div>
                    )}
                    {!(s.father?.name) && !(s.mother?.name) && (
                      <span className="text-[#A0AEC0]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
                      {can("view_receipts") && (
                        <button
                          onClick={() => setReceiptsStudent(s)}
                          className="text-[11px] font-semibold text-[#0F1B47] px-3 py-1.5 rounded-full bg-[#C6D947]/30 hover:bg-[#C6D947]/50 inline-flex items-center gap-1"
                          data-testid={`student-receipts-${s.id}`}
                          title="Fees & receipts"
                        >
                          <Receipt size={11} /> Fees
                        </button>
                      )}
                      {(can("view_receipts") || can("manage_fees")) && (
                        <button
                          onClick={() => setLedgerStudent(s)}
                          className="text-[11px] font-semibold text-[#0F1B47] px-3 py-1.5 rounded-full bg-[#E3EDF9] hover:bg-[#CFE0F0] inline-flex items-center gap-1"
                          data-testid={`student-ledger-${s.id}`}
                          title="Open student ledger"
                        >
                          <Wallet size={11} /> Ledger
                        </button>
                      )}
                      {can("view_students") && (
                        <div className="relative inline-block">
                          <button
                            onClick={() => setOpenMenuId(openMenuId === s.id ? null : s.id)}
                            className="p-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10"
                            data-testid={`student-menu-${s.id}`}
                            title="More actions"
                          >
                            <MoreVertical size={14} />
                          </button>
                          {openMenuId === s.id && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />
                              <div className="absolute right-0 mt-2 w-56 bg-[#FDFDF9] rounded-2xl border border-[#0F1B47]/8 shadow-xl z-20 py-1.5">
                                {can("edit_students") && (
                                  <MenuItem icon={<Edit3 size={14} />} label="Edit profile" onClick={() => { setEditing(s); setShowForm(true); setOpenMenuId(null); }} testid={`student-edit-${s.id}`} />
                                )}
                                {can("manage_documents") && (
                                  <MenuItem icon={<Paperclip size={14} />} label="View / Add documents" onClick={() => { setDocsStudent(s); setOpenMenuId(null); }} testid={`student-docs-${s.id}`} />
                                )}
                                {(can("manage_term_reports") || can("view_students")) && (
                                  <MenuItem icon={<FileText size={14} />} label="Term reports" onClick={() => { setTermReportsStudent(s); setOpenMenuId(null); }} testid={`student-term-reports-${s.id}`} />
                                )}
                                {can("manage_attendance") && (
                                  <MenuItem icon={<CalendarDays size={14} />} label="Attendance" onClick={() => { setAttendStudent(s); setOpenMenuId(null); }} testid={`student-attendance-${s.id}`} />
                                )}
                                <MenuItem icon={<IdCard size={14} />} label="Download ID Card" onClick={() => { downloadIdCard(s); setOpenMenuId(null); }} testid={`student-idcard-${s.id}`} />
                                <MenuItem icon={<IdCard size={14} />} label="Download Parent ID Card" onClick={() => { downloadParentIdCard(s); setOpenMenuId(null); }} testid={`student-parent-idcard-${s.id}`} />
                                {can("edit_students") && (
                                  <>
                                    <div className="my-1 border-t border-[#0F1B47]/8" />
                                    {(s.status === "withdrawn" || s.status === "passed_out" || s.status === "disabled") ? (
                                      <MenuItem icon={<Power size={14} />} label="Restore to active" onClick={() => { toggleStatus(s, "active"); setOpenMenuId(null); }} testid={`student-restore-${s.id}`} />
                                    ) : (
                                      <>
                                        <MenuItem icon={<Ban size={14} />} label="Disable" onClick={() => { toggleStatus(s, "disabled"); setOpenMenuId(null); }} />
                                        <MenuItem icon={<FileText size={14} />} label="Mark withdrawn" onClick={() => { toggleStatus(s, "withdrawn"); setOpenMenuId(null); }} testid={`student-withdraw-${s.id}`} />
                                        <MenuItem icon={<GraduationCap size={14} />} label="Mark passed out" onClick={() => { toggleStatus(s, "passed_out"); setOpenMenuId(null); }} testid={`student-passout-${s.id}`} />
                                      </>
                                    )}
                                    <MenuItem danger icon={<Trash2 size={14} />} label="Delete permanently" onClick={() => { removeStudent(s); setOpenMenuId(null); }} />
                                  </>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <StudentFormDrawer
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
      {showPromote && (
        <PromoteStudentsModal
          fromSession={session !== "all" ? session : currentSessionLabel()}
          onClose={() => setShowPromote(false)}
          onDone={() => { setShowPromote(false); load(); }}
        />
      )}

      {ledgerStudent && (
        <LedgerDrawer
          student={ledgerStudent}
          can={can}
          onClose={() => { setLedgerStudent(null); load(); }}
        />
      )}

      {docsStudent && (
        <DocumentsDrawer
          student={docsStudent}
          can={can}
          onClose={() => setDocsStudent(null)}
        />
      )}

      {termReportsStudent && (
        <TermReportsDrawer
          student={termReportsStudent}
          can={can}
          onClose={() => setTermReportsStudent(null)}
        />
      )}

      {attendStudent && (
        <AttendanceDrawer
          student={attendStudent}
          onClose={() => setAttendStudent(null)}
        />
      )}

      {receiptsStudent && (
        <ReceiptsDrawer
          student={receiptsStudent}
          can={can}
          onClose={() => { setReceiptsStudent(null); load(); }}
        />
      )}
    </div>
  );
}

function MenuItem({ icon, label, onClick, danger, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left ${danger ? "text-red-700 hover:bg-red-50" : "text-[#0F1B47] hover:bg-[#0F1B47]/5"}`}
    >
      {icon}
      {label}
    </button>
  );
}

function Avatar({ url, name }) {
  if (url) {
    return <img src={url} alt={name} className="w-14 h-14 rounded-2xl object-cover bg-[#F5F5F0] flex-shrink-0" />;
  }
  const initials = (name || "?").split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  return (
    <div className="w-14 h-14 rounded-2xl bg-[#E3EDF9] text-[#0F1B47] font-semibold text-lg flex items-center justify-center flex-shrink-0">
      {initials}
    </div>
  );
}

function PhotoControl({ photoUrl, studentId, studentName, isEdit, onChange }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);
  const initials = (studentName || "?").split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();

  const pickFile = () => fileRef.current?.click();

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type) && !/\.(jpe?g|png|webp)$/i.test(file.name)) {
      toast.error("Please pick a JPG, PNG or WEBP image.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is over 8 MB. Pick a smaller one.");
      return;
    }
    // New student (no id yet) — read locally as data URL and let the form save it on submit
    if (!isEdit || !studentId) {
      const reader = new FileReader();
      reader.onload = () => {
        onChange(String(reader.result || ""));
        toast.success("Photo attached. Save the form to keep it.");
      };
      reader.onerror = () => toast.error("Could not read file");
      reader.readAsDataURL(file);
      return;
    }
    // Existing student — upload to the backend (server-side resize + persist)
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post(`/admin/students/${studentId}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.photo_url || "");
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const clearPhoto = async () => {
    if (isEdit && studentId && photoUrl) {
      try {
        await adminApi.delete(`/admin/students/${studentId}/photo`);
      } catch {/* fall through */}
    }
    onChange("");
  };

  return (
    <div className="flex items-start gap-4">
      <div className="w-20 h-20 rounded-2xl bg-[#F5F5F0] border border-[#0F1B47]/8 overflow-hidden flex-shrink-0 flex items-center justify-center">
        {photoUrl ? (
          <img src={photoUrl} alt={studentName || "Student"} className="w-full h-full object-cover" data-testid="student-photo-preview" />
        ) : (
          <span className="text-[#0F1B47] font-semibold text-lg">{initials || "?"}</span>
        )}
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
            data-testid="student-photo-file"
          />
          <button
            type="button"
            onClick={pickFile}
            disabled={uploading}
            className="inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-4 py-2 font-semibold text-[13px] hover:bg-[#1a2b6b] disabled:opacity-50"
            data-testid="student-photo-upload-btn"
          >
            <Camera size={14} /> {uploading ? "Uploading…" : photoUrl ? "Replace photo" : "Upload from computer"}
          </button>
          {photoUrl && (
            <button
              type="button"
              onClick={clearPhoto}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-red-600 px-3 py-1.5 rounded-full hover:bg-red-50"
              data-testid="student-photo-remove-btn"
            >
              <Trash2 size={12} /> Remove
            </button>
          )}
        </div>
        <input
          value={photoUrl?.startsWith("data:") ? "" : (photoUrl || "")}
          onChange={(e) => onChange(e.target.value)}
          placeholder={photoUrl?.startsWith("data:") ? "Uploaded photo · use Replace or Remove to change" : "…or paste an image URL"}
          disabled={photoUrl?.startsWith("data:")}
          className={`${inputCls} text-[13px] py-2 disabled:bg-[#F5F5F0] disabled:text-[#A0AEC0]`}
          data-testid="student-photo-url"
        />
        <p className="text-[11px] text-[#718096]">JPG, PNG or WEBP · max 8 MB · resized to 512×512 on upload</p>
      </div>
    </div>
  );
}

function ParentPhotoControl({ role, photoUrl, studentId, parentName, isEdit, onChange }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);
  const initials = (parentName || "?").split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();

  const pickFile = () => fileRef.current?.click();

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type) && !/\.(jpe?g|png|webp)$/i.test(file.name)) {
      toast.error("Please pick a JPG, PNG or WEBP image.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is over 8 MB. Pick a smaller one.");
      return;
    }
    if (!isEdit || !studentId) {
      const reader = new FileReader();
      reader.onload = () => {
        onChange(String(reader.result || ""));
        toast.success(`${role[0].toUpperCase() + role.slice(1)}'s photo attached. Save the form to keep it.`);
      };
      reader.onerror = () => toast.error("Could not read file");
      reader.readAsDataURL(file);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post(`/admin/students/${studentId}/parent-photo/${role}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.photo_url || "");
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const clearPhoto = async () => {
    if (isEdit && studentId && photoUrl) {
      try { await adminApi.delete(`/admin/students/${studentId}/parent-photo/${role}`); } catch {/* ignore */}
    }
    onChange("");
  };

  return (
    <div className="flex items-start gap-3" data-testid={`parent-photo-${role}`}>
      <div className="w-16 h-16 rounded-2xl bg-[#F5F5F0] border border-[#0F1B47]/8 overflow-hidden flex-shrink-0 flex items-center justify-center">
        {photoUrl ? (
          <img src={photoUrl} alt={parentName || role} className="w-full h-full object-cover" />
        ) : (
          <span className="text-[#0F1B47] font-semibold text-base">{initials || (role === "father" ? "F" : "M")}</span>
        )}
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => handleFile(e.target.files?.[0])}
          className="hidden"
          data-testid={`parent-photo-file-${role}`}
        />
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={pickFile}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-3.5 py-1.5 font-semibold text-[12px] hover:bg-[#1a2b6b] disabled:opacity-50"
            data-testid={`parent-photo-upload-${role}`}
          >
            <Camera size={12} /> {uploading ? "Uploading…" : photoUrl ? "Replace" : "Upload"}
          </button>
          {photoUrl && (
            <button
              type="button"
              onClick={clearPhoto}
              disabled={uploading}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 px-2 py-1 rounded-full hover:bg-red-50"
              data-testid={`parent-photo-remove-${role}`}
            >
              <Trash2 size={11} /> Remove
            </button>
          )}
        </div>
        <p className="text-[10px] text-[#718096]">JPG, PNG or WEBP · max 8 MB</p>
      </div>
    </div>
  );
}



/* ===== Add / edit drawer ===== */

function StudentFormDrawer({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    full_name: initial?.full_name || "",
    admission_id: initial?.admission_id || "",
    dob: initial?.dob || "",
    gender: initial?.gender || "",
    class: initial?.class || "Toddlers",
    monthly_fee: initial?.monthly_fee ?? "",
    photo_url: initial?.photo_url || "",
    address: initial?.address || "",
    nationality: initial?.nationality || "Indian",
    mother_tongue: initial?.mother_tongue || "",
    blood_group: initial?.blood_group || "",
    notes: initial?.notes || "",
    session: initial?.session || currentSessionLabel(),
    father: { relation: initial?.father?.relation || "Father", name: initial?.father?.name || "", phone: initial?.father?.phone || "", occupation: initial?.father?.occupation || "", email: initial?.father?.email || "", photo_url: initial?.father?.photo_url || "" },
    mother: { relation: initial?.mother?.relation || "Mother", name: initial?.mother?.name || "", phone: initial?.mother?.phone || "", occupation: initial?.mother?.occupation || "", email: initial?.mother?.email || "", photo_url: initial?.mother?.photo_url || "" },
    fee_structure: {
      frequency: initial?.fee_structure?.frequency || "monthly",
      components: {
        application: initial?.fee_structure?.components?.application ?? 1000,
        registration: initial?.fee_structure?.components?.registration ?? 0,
        security: initial?.fee_structure?.components?.security ?? 0,
        tuition: initial?.fee_structure?.components?.tuition ?? 0,
      },
    },
  });

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setNested = (parent, k, v) => setF((p) => ({ ...p, [parent]: { ...p[parent], [k]: v } }));
  const setFee = (k, v) => setF((p) => ({ ...p, fee_structure: { ...p.fee_structure, [k]: v } }));
  const setFeeComp = (k, v) => setF((p) => ({
    ...p,
    fee_structure: {
      ...p.fee_structure,
      components: { ...p.fee_structure.components, [k]: v === "" ? "" : Number(v) },
    },
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.full_name.trim()) return toast.error("Name is required");
    if (!f.admission_id.trim()) return toast.error("Admission ID is required");
    if (!f.dob) return toast.error("Date of birth is required");
    setSaving(true);
    try {
      const comps = f.fee_structure.components;
      const body = {
        ...f,
        admission_id: f.admission_id.trim(),
        monthly_fee: f.monthly_fee === "" || f.monthly_fee === null ? 0 : Number(f.monthly_fee),
        fee_structure: {
          frequency: f.fee_structure.frequency,
          components: {
            application: Number(comps.application || 0),
            registration: Number(comps.registration || 0),
            security: Number(comps.security || 0),
            tuition: Number(comps.tuition || 0),
          },
        },
      };
      if (isEdit) {
        await adminApi.patch(`/admin/students/${initial.id}`, body);
        toast.success("Student updated");
      } else {
        await adminApi.post("/admin/students", body);
        toast.success("Student added");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer title={isEdit ? `Edit · ${initial.full_name}` : "Add a new student"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-6" data-testid="student-form">
        <Section title="Child">
          <Grid>
            <Field label="Full name *">
              <input value={f.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} required data-testid="student-form-name" />
            </Field>
            <Field label="Admission ID *">
              <input
                value={f.admission_id}
                onChange={(e) => set("admission_id", e.target.value)}
                className={`${inputCls} font-mono tracking-wide`}
                placeholder="e.g. NEY/297/26-27/012"
                required
                maxLength={40}
                data-testid="student-form-admission-id"
              />
            </Field>
            <Field label="Date of birth *">
              <input type="date" value={f.dob} onChange={(e) => set("dob", e.target.value)} className={inputCls} required data-testid="student-form-dob" />
            </Field>
            <Field label="Gender">
              <select value={f.gender} onChange={(e) => set("gender", e.target.value)} className={inputCls}>
                <option value="">—</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </Field>
            <Field label="Class *">
              <select value={f.class} onChange={(e) => set("class", e.target.value)} className={inputCls} required data-testid="student-form-class">
                {CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Session *">
              <select value={f.session} onChange={(e) => set("session", e.target.value)} className={inputCls} required data-testid="student-form-session">
                {sessionOptions().map((s) => <option key={s} value={s}>{s.replace("-", "–")}</option>)}
              </select>
            </Field>
            <Field label="Monthly fee (₹)">
              <input type="number" min="0" step="1" value={f.monthly_fee} onChange={(e) => set("monthly_fee", e.target.value)} placeholder="Optional — leave blank if not set" className={inputCls} data-testid="student-form-fee" />
            </Field>
            <Field label="Photo" full>
              <PhotoControl
                photoUrl={f.photo_url}
                studentId={initial?.id}
                studentName={f.full_name || initial?.full_name}
                isEdit={isEdit}
                onChange={(url) => set("photo_url", url)}
              />
            </Field>
            <Field label="Nationality">
              <input value={f.nationality} onChange={(e) => set("nationality", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Mother tongue">
              <input value={f.mother_tongue} onChange={(e) => set("mother_tongue", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Blood group">
              <input value={f.blood_group} onChange={(e) => set("blood_group", e.target.value)} placeholder="e.g. O+" className={inputCls} />
            </Field>
          </Grid>
        </Section>

        <div>
          <div className="flex items-center gap-3 mb-3">
            <span className="text-[11px] font-bold tracking-[0.16em] uppercase text-[#648DE5]">Parent / Guardian 1</span>
            <select
              value={f.father.relation}
              onChange={(e) => setNested("father", "relation", e.target.value)}
              className="text-[11px] font-semibold bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-full px-3 py-1 text-[#0F1B47] focus:outline-none focus:border-[#648DE5]"
              data-testid="student-form-father-relation"
            >
              <option value="Father">Father</option>
              <option value="Guardian 1">Guardian 1</option>
            </select>
          </div>
          <Grid>
            <Field label="Name"><input value={f.father.name} onChange={(e) => setNested("father", "name", e.target.value)} className={inputCls} /></Field>
            <Field label="Phone"><input value={f.father.phone} onChange={(e) => setNested("father", "phone", e.target.value)} className={inputCls} /></Field>
            <Field label="Occupation"><input value={f.father.occupation} onChange={(e) => setNested("father", "occupation", e.target.value)} className={inputCls} /></Field>
            <Field label="Email"><input type="email" value={f.father.email} onChange={(e) => setNested("father", "email", e.target.value)} className={inputCls} /></Field>
            <Field label="Photo" full>
              <ParentPhotoControl
                role="father"
                photoUrl={f.father.photo_url}
                studentId={initial?.id}
                parentName={f.father.name}
                isEdit={isEdit}
                onChange={(url) => setNested("father", "photo_url", url)}
              />
            </Field>
          </Grid>
        </div>

        <div>
          <div className="flex items-center gap-3 mb-3">
            <span className="text-[11px] font-bold tracking-[0.16em] uppercase text-[#648DE5]">Parent / Guardian 2</span>
            <select
              value={f.mother.relation}
              onChange={(e) => setNested("mother", "relation", e.target.value)}
              className="text-[11px] font-semibold bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-full px-3 py-1 text-[#0F1B47] focus:outline-none focus:border-[#648DE5]"
              data-testid="student-form-mother-relation"
            >
              <option value="Mother">Mother</option>
              <option value="Guardian 2">Guardian 2</option>
            </select>
          </div>
          <Grid>
            <Field label="Name"><input value={f.mother.name} onChange={(e) => setNested("mother", "name", e.target.value)} className={inputCls} /></Field>
            <Field label="Phone"><input value={f.mother.phone} onChange={(e) => setNested("mother", "phone", e.target.value)} className={inputCls} /></Field>
            <Field label="Occupation"><input value={f.mother.occupation} onChange={(e) => setNested("mother", "occupation", e.target.value)} className={inputCls} /></Field>
            <Field label="Email"><input type="email" value={f.mother.email} onChange={(e) => setNested("mother", "email", e.target.value)} className={inputCls} /></Field>
            <Field label="Photo" full>
              <ParentPhotoControl
                role="mother"
                photoUrl={f.mother.photo_url}
                studentId={initial?.id}
                parentName={f.mother.name}
                isEdit={isEdit}
                onChange={(url) => setNested("mother", "photo_url", url)}
              />
            </Field>
          </Grid>
        </div>

        <Section title="Fee structure">
          <div className="bg-[#F5F5F0] rounded-2xl p-4 md:p-5">
            <div className="text-xs text-[#4A5568] mb-3">
              Annual amounts (in INR). The receipt PDF always shows all four lines — set 0 for any not applicable.
            </div>
            <div className="mb-4">
              <label className="block text-[11px] font-bold tracking-[0.16em] uppercase text-[#0F1B47] mb-2">Payment plan</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { v: "yearly", l: "Yearly" },
                  { v: "term", l: "Term-wise" },
                  { v: "monthly", l: "Monthly" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setFee("frequency", opt.v)}
                    className={`text-xs font-semibold rounded-full px-3.5 py-1.5 border transition-colors ${
                      f.fee_structure.frequency === opt.v
                        ? "bg-[#0F1B47] text-[#FDFDF9] border-[#0F1B47]"
                        : "bg-[#FDFDF9] text-[#0F1B47] border-[#0F1B47]/15 hover:border-[#0F1B47]/40"
                    }`}
                    data-testid={`student-fee-freq-${opt.v}`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>
            <Grid>
              <Field label="Application fee (₹)">
                <input type="number" min="0" step="1" value={f.fee_structure.components.application} onChange={(e) => setFeeComp("application", e.target.value)} className={inputCls} data-testid="student-fee-application" />
              </Field>
              <Field label="Registration fee (₹)">
                <input type="number" min="0" step="1" value={f.fee_structure.components.registration} onChange={(e) => setFeeComp("registration", e.target.value)} className={inputCls} data-testid="student-fee-registration" />
              </Field>
              <Field label="Security deposit (₹)">
                <input type="number" min="0" step="1" value={f.fee_structure.components.security} onChange={(e) => setFeeComp("security", e.target.value)} className={inputCls} data-testid="student-fee-security" />
              </Field>
              <Field label="Tuition fee · annual (₹)">
                <input type="number" min="0" step="1" value={f.fee_structure.components.tuition} onChange={(e) => setFeeComp("tuition", e.target.value)} className={inputCls} data-testid="student-fee-tuition" />
              </Field>
            </Grid>
          </div>
        </Section>

        <Section title="Other">
          <Grid>
            <Field full label="Address">
              <textarea value={f.address} onChange={(e) => set("address", e.target.value)} rows={2} className={inputCls} />
            </Field>
            <Field full label="Notes">
              <textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className={inputCls} placeholder="Allergies, special needs, etc." />
            </Field>
          </Grid>
        </Section>

        <div className="flex items-center gap-3 pt-2 sticky bottom-0 bg-[#FDFDF9] py-3 -mx-6 px-6 border-t border-[#0F1B47]/8">
          <button type="submit" disabled={saving} className="bg-[#0F1B47] text-[#FDFDF9] rounded-full px-6 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b] disabled:opacity-50" data-testid="student-form-save">
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Add student")}
          </button>
          <button type="button" onClick={onClose} className="text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10">Cancel</button>
        </div>
      </form>
    </Drawer>
  );
}

/* ===== Ledger drawer ===== */

function LedgerDrawer({ student, can, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGen, setShowGen] = useState(false);
  const [showPay, setShowPay] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get(`/admin/students/${student.id}/ledger`);
      setData(res.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const removeEntry = async (eid) => {
    if (!window.confirm("Delete this ledger entry?")) return;
    try {
      await adminApi.delete(`/admin/students/${student.id}/ledger/${eid}`);
      toast.success("Entry deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Drawer title={`Ledger · ${student.full_name}`} subtitle={`${student.admission_id} · ${student.class} · Monthly fee ${INR(student.monthly_fee)}`} onClose={onClose}>
      {loading || !data ? (
        <div className="text-sm text-[#718096] py-6 text-center">Loading ledger…</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3 mb-5">
            <SummaryStat label="Total fee" value={INR(data.total_fee)} tone="rose" />
            <SummaryStat label="Total paid" value={INR(data.total_paid)} tone="teal" />
            <SummaryStat label="Balance" value={INR(Math.max(data.balance || 0, 0))} tone={data.balance > 0 ? "amber" : "green"} />
          </div>

          {can("manage_fees") && (
            <div className="flex flex-wrap gap-2 mb-5">
              <button onClick={() => setShowGen(true)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#FFE9E0] hover:bg-[#FFD4C0]" data-testid="ledger-generate-btn">
                <CreditCard size={14} /> Generate fee
              </button>
              <button onClick={() => setShowPay(true)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#DCEFE1] hover:bg-[#BDDFC4]" data-testid="ledger-pay-btn">
                <Check size={14} /> Record payment
              </button>
            </div>
          )}

          <div className="bg-[#F5F5F0] rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0F1B47]/5">
                <tr className="text-left text-[11px] font-bold tracking-[0.16em] uppercase text-[#0F1B47]">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Description</th>
                  <th className="px-4 py-3 text-right">Fee</th>
                  <th className="px-4 py-3 text-right">Paid</th>
                  <th className="px-4 py-3">Period</th>
                  {can("manage_fees") && <th className="px-2 py-3" />}
                </tr>
              </thead>
              <tbody data-testid="ledger-table">
                {data.entries.length === 0 ? (
                  <tr>
                    <td colSpan={can("manage_fees") ? 6 : 5} className="px-4 py-10 text-center text-[#718096] text-sm">
                      No entries yet. Click "Generate fee" to record the first month's fee.
                    </td>
                  </tr>
                ) : data.entries.map((e) => (
                  <tr key={e.id} className="border-t border-[#0F1B47]/8">
                    <td className="px-4 py-3 whitespace-nowrap text-[#0F1B47]">{e.date}</td>
                    <td className="px-4 py-3 text-[#0F1B47]">
                      {e.type === "generated" ? `Fee Generated for ${e.period}`
                        : e.type === "paid" ? `Fee Paid for ${e.period}`
                        : `Adjustment · ${e.period}`}
                      {e.note ? <div className="text-[11px] text-[#718096] mt-0.5">{e.note}</div> : null}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {e.type === "generated" ? <span className="text-rose-600 font-semibold">{INR(e.amount)}</span> : <span className="text-[#718096]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {e.type === "paid" ? <span className="text-teal-700 font-semibold">{INR(e.amount)}</span> : <span className="text-[#718096]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[#0F1B47] whitespace-nowrap">
                      {e.period}
                      {e.payment_mode && <div className="text-[10px] text-[#718096] uppercase tracking-wide">{e.payment_mode}{e.ref_no ? ` · ${e.ref_no}` : ""}</div>}
                    </td>
                    {can("manage_fees") && (
                      <td className="px-2 py-3 text-right">
                        <button onClick={() => removeEntry(e.id)} className="text-red-600 hover:text-red-700" title="Delete entry">
                          <Trash2 size={13} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {showGen && (
            <LedgerEntryModal
              title="Generate fee"
              defaultAmount={student.monthly_fee}
              type="generated"
              studentId={student.id}
              onClose={() => setShowGen(false)}
              onSaved={() => { setShowGen(false); load(); }}
            />
          )}
          {showPay && (
            <LedgerEntryModal
              title="Record payment"
              defaultAmount={Math.max(data.balance, 0) || student.monthly_fee}
              type="paid"
              studentId={student.id}
              onClose={() => setShowPay(false)}
              onSaved={() => { setShowPay(false); load(); }}
            />
          )}
        </>
      )}
    </Drawer>
  );
}

/* ===== Documents drawer ===== */

function DocumentsDrawer({ student, can, onClose }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("Other");
  const [note, setNote] = useState("");
  const inputRef = React.useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get(`/admin/students/${student.id}/documents`);
      setDocs(res.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("File too large (max 5 MB)");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    fd.append("note", note);
    setUploading(true);
    try {
      await adminApi.post(`/admin/students/${student.id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const downloadDoc = async (d) => {
    try {
      const res = await adminApi.get(`/admin/students/${student.id}/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const removeDoc = async (d) => {
    if (!window.confirm(`Delete "${d.filename}"?`)) return;
    try {
      await adminApi.delete(`/admin/students/${student.id}/documents/${d.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Drawer
      title={`Documents · ${student.full_name}`}
      subtitle={`${student.admission_id}`}
      onClose={onClose}
    >
      {can("manage_documents") && (
        <div className="bg-[#F5F5F0] rounded-2xl p-4 md:p-5 mb-5" data-testid="docs-uploader">
          <div className="text-[11px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mb-3">Upload a document</div>
          <Grid>
            <Field label="Category">
              <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls} data-testid="docs-category">
                {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Note (optional)">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. issued 2024-06-12" className={inputCls} />
            </Field>
          </Grid>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <input
              ref={inputRef}
              type="file"
              onChange={(e) => handleUpload(e.target.files?.[0])}
              className="hidden"
              data-testid="docs-file-input"
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b] disabled:opacity-50"
              data-testid="docs-upload-btn"
            >
              <Upload size={14} /> {uploading ? "Uploading…" : "Choose file & upload"}
            </button>
            <span className="text-[11px] text-[#718096]">Max 5 MB. PDF, images, common files.</span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center text-[#718096] text-sm py-6">Loading documents…</div>
      ) : docs.length === 0 ? (
        <div className="text-center text-[#718096] text-sm py-10">No documents uploaded yet.</div>
      ) : (
        <ul className="space-y-2" data-testid="docs-list">
          {docs.map((d) => (
            <li key={d.id} className="bg-[#F5F5F0] rounded-2xl p-4 flex items-center gap-3" data-testid={`doc-row-${d.id}`}>
              <div className="w-10 h-10 rounded-xl bg-[#FDFDF9] flex items-center justify-center text-[#0F1B47]">
                <FileText size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#0F1B47] text-[14px] truncate">{d.filename}</div>
                <div className="text-[11px] text-[#718096] mt-0.5">
                  {d.category} · {formatBytes(d.size)} · {new Date(d.uploaded_at).toLocaleDateString()}
                </div>
                {d.note && <div className="text-[11px] text-[#4A5568] mt-0.5 italic">{d.note}</div>}
              </div>
              <button
                onClick={() => downloadDoc(d)}
                className="text-xs font-semibold text-[#0F1B47] px-3 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10 inline-flex items-center gap-1"
                data-testid={`doc-download-${d.id}`}
              >
                <FileDown size={12} /> Download
              </button>
              {can("manage_documents") && (
                <button
                  onClick={() => removeDoc(d)}
                  className="text-red-600 hover:text-red-700 p-2"
                  title="Delete"
                  data-testid={`doc-delete-${d.id}`}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Drawer>
  );
}

/* ===== Per-student attendance calendar drawer ===== */

const STATUS_COLORS = {
  present: "bg-emerald-100 text-emerald-700 border-emerald-300",
  absent: "bg-rose-100 text-rose-700 border-rose-300",
  late: "bg-amber-100 text-amber-700 border-amber-300",
  leave: "bg-sky-100 text-sky-700 border-sky-300",
  holiday: "bg-violet-100 text-violet-700 border-violet-300",
};

function AttendanceDrawer({ student, onClose }) {
  const today = new Date();
  const [month, setMonth] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get(`/admin/students/${student.id}/attendance`, { params: { month } });
      setData(res.data);
    } catch (e) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [month]); // eslint-disable-line

  const calendar = useMemo(() => {
    if (!data) return [];
    const [y, m] = month.split("-").map(Number);
    const firstDay = new Date(y, m - 1, 1).getDay(); // 0 Sun
    const daysInMonth = new Date(y, m, 0).getDate();
    const byDate = {};
    (data.entries || []).forEach((e) => { byDate[e.date] = e; });
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const ds = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      cells.push({ day: d, date: ds, mark: byDate[ds] });
    }
    return cells;
  }, [data, month]);

  return (
    <Drawer title={`Attendance · ${student.full_name}`} subtitle={`${student.admission_id} · ${student.class}`} onClose={onClose}>
      <div className="flex items-center gap-3 mb-4">
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="bg-[#F5F5F0] border border-[#0F1B47]/10 rounded-full px-4 py-2 text-sm focus:outline-none"
          data-testid="attendance-drawer-month"
        />
      </div>

      {loading || !data ? (
        <div className="text-center text-[#718096] text-sm py-6">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-5 gap-2 mb-5">
            <Pill label="Present" value={data.counts.present} cls="bg-emerald-50 text-emerald-700" />
            <Pill label="Absent" value={data.counts.absent} cls="bg-rose-50 text-rose-700" />
            <Pill label="Late" value={data.counts.late} cls="bg-amber-50 text-amber-700" />
            <Pill label="Leave" value={data.counts.leave} cls="bg-sky-50 text-sky-700" />
            <Pill label="Holiday" value={data.counts.holiday} cls="bg-violet-50 text-violet-700" />
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold tracking-[0.16em] uppercase text-[#718096] mb-2">
            {["S","M","T","W","T","F","S"].map((d, i) => <div key={i}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {calendar.map((cell, i) => {
              if (!cell) return <div key={i} />;
              const status = cell.mark?.status;
              const cls = status ? STATUS_COLORS[status] : "bg-[#F5F5F0] text-[#718096] border-[#0F1B47]/10";
              return (
                <div key={cell.date} className={`aspect-square rounded-lg border text-[12px] font-semibold flex flex-col items-center justify-center ${cls}`} title={status || "Not marked"}>
                  <div>{cell.day}</div>
                  {status && <div className="text-[8px] uppercase tracking-wider mt-0.5">{status[0]}</div>}
                </div>
              );
            })}
          </div>
        </>
      )}
    </Drawer>
  );
}

function Pill({ label, value, cls }) {
  return (
    <div className={`rounded-2xl px-3 py-2 text-center ${cls}`}>
      <div className="text-[9px] font-bold tracking-[0.18em] uppercase opacity-80">{label}</div>
      <div className="text-base font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function LedgerEntryModal({ title, type, defaultAmount, studentId, onClose, onSaved }) {
  const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];  const today = new Date();
  const [period, setPeriod] = useState(`${months[today.getMonth()]} ${today.getFullYear()}`);
  const [amount, setAmount] = useState(defaultAmount || "");
  const [datev, setDatev] = useState(today.toISOString().slice(0, 10));
  const [mode, setMode] = useState("Online");
  const [refNo, setRefNo] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!period.trim() || !amount) return toast.error("Period and amount are required");
    setSaving(true);
    try {
      const body = { type, period, amount: Number(amount), date: datev, note };
      if (type === "paid") { body.payment_mode = mode; body.ref_no = refNo; }
      await adminApi.post(`/admin/students/${studentId}/ledger`, body);
      toast.success(type === "paid" ? "Payment recorded" : "Fee generated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F1B47]/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-[#FDFDF9] rounded-3xl w-full max-w-lg p-6 shadow-2xl" data-testid="ledger-entry-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl text-[#0F1B47] font-medium">{title}</h3>
          <button type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-[#0F1B47]/5"><X size={16} /></button>
        </div>
        <Grid>
          <Field label="Period (e.g. June 2026)">
            <input value={period} onChange={(e) => setPeriod(e.target.value)} className={inputCls} required data-testid="ledger-period" />
          </Field>
          <Field label="Amount (₹)">
            <input type="number" min="0" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} required data-testid="ledger-amount" />
          </Field>
          <Field label="Date">
            <input type="date" value={datev} onChange={(e) => setDatev(e.target.value)} className={inputCls} />
          </Field>
          {type === "paid" && (
            <>
              <Field label="Payment mode">
                <select value={mode} onChange={(e) => setMode(e.target.value)} className={inputCls}>
                  {PAY_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
              <Field full label="Reference / transaction no.">
                <input value={refNo} onChange={(e) => setRefNo(e.target.value)} className={inputCls} placeholder="optional" />
              </Field>
            </>
          )}
          <Field full label="Note">
            <input value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} placeholder="optional" />
          </Field>
        </Grid>
        <div className="flex items-center gap-3 mt-5">
          <button type="submit" disabled={saving} className="bg-[#0F1B47] text-[#FDFDF9] rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b] disabled:opacity-50" data-testid="ledger-save">
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={onClose} className="text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10">Cancel</button>
        </div>
      </form>
    </div>
  );
}

/* ===== Reusable bits ===== */

const inputCls = "w-full bg-[#FDFDF9] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14.5px] focus:outline-none focus:border-[#648DE5]";

function Drawer({ title, subtitle, onClose, children }) {
  return (
    <div className="fixed inset-0 z-40 bg-[#0F1B47]/40" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="fixed top-0 right-0 h-full w-full md:w-[720px] bg-[#FDFDF9] shadow-2xl overflow-y-auto" data-testid="student-drawer">
        <div className="sticky top-0 z-10 bg-[#FDFDF9] border-b border-[#0F1B47]/8 px-6 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-display text-xl text-[#0F1B47] font-medium truncate">{title}</h3>
            {subtitle && <p className="text-xs text-[#4A5568] mt-1 truncate">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#0F1B47]/5" data-testid="drawer-close">
            <X size={16} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] font-bold tracking-[0.16em] uppercase text-[#648DE5] mb-3">{title}</div>
      {children}
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[11px] font-bold tracking-[0.16em] uppercase text-[#0F1B47] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function SummaryStat({ label, value, tone }) {
  const tones = {
    rose: "bg-rose-50 text-rose-700",
    teal: "bg-teal-50 text-teal-700",
    amber: "bg-amber-50 text-amber-700",
    green: "bg-emerald-50 text-emerald-700",
  };
  return (
    <div className={`rounded-2xl px-4 py-3 ${tones[tone] || tones.rose}`}>
      <div className="text-[10px] font-bold tracking-[0.18em] uppercase opacity-80">{label}</div>
      <div className="text-xl font-semibold mt-0.5">{value}</div>
    </div>
  );
}

/* ===== Receipts drawer ===== */

const RECEIPT_KEYS = ["application", "registration", "security", "tuition"];
const RECEIPT_LABELS = {
  application: "Application Fee",
  registration: "Registration Fee",
  security: "Security Deposit",
  tuition: "Tuition Fee",
};

function ReceiptsDrawer({ student, can, onClose }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [showRaise, setShowRaise] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get(`/admin/students/${student.id}/receipts`);
      setList(res.data || []);
    } catch (e) {
      // 401 handled globally
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const downloadPdf = async (rid, no) => {
    try {
      const res = await adminApi.get(`/admin/receipts/${rid}.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `naavi-receipt-${(no || rid).replace(/\//g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  const removeReceipt = async (r) => {
    if (!window.confirm(`Delete receipt ${r.receipt_no}? The linked payment entry will also be removed from the ledger.`)) return;
    try {
      await adminApi.delete(`/admin/receipts/${r.id}`);
      toast.success("Receipt deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Drawer
      title={`Receipts · ${student.full_name}`}
      subtitle={`${student.admission_id} · ${student.class}`}
      onClose={onClose}
    >
      {can("generate_receipts") && (
        <div className="flex flex-wrap justify-end gap-2 mb-5">
          {can("manage_fees") && (
            <button
              onClick={() => setShowRaise(true)}
              className="inline-flex items-center gap-2 bg-[#C6D947] text-[#0F1B47] rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#d6e860]"
              data-testid="raise-fee-btn"
              title="Send a pending fee demand to the parent's portal"
            >
              <Wallet size={14} /> Raise fee
            </button>
          )}
          <button
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b]"
            data-testid="receipts-new-btn"
          >
            <Receipt size={14} /> Generate receipt
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-[#718096] py-6 text-center">Loading receipts…</div>
      ) : list.length === 0 ? (
        <div className="bg-[#F5F5F0] rounded-2xl p-10 text-center">
          <Receipt size={28} className="mx-auto text-[#648DE5] mb-2" />
          <div className="text-[#0F1B47] font-medium">No receipts yet</div>
          <p className="text-xs text-[#718096] mt-1">{can("generate_receipts") ? "Click \"Generate receipt\" to create the first one." : "Ask a fee manager to generate one."}</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="receipts-list">
          {list.map((r) => (
            <li key={r.id} className="bg-[#F5F5F0] rounded-2xl p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`receipt-row-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-mono text-[12px] text-[#0F1B47] font-semibold">{r.receipt_no}</div>
                  <span className="text-[10px] font-bold tracking-[0.16em] uppercase rounded-full px-2 py-0.5 border bg-[#C6D947]/30 text-[#0F1B47] border-[#C6D947]/60">
                    {INR(r.total)}
                  </span>
                </div>
                <div className="text-sm text-[#0F1B47] mt-1">{r.period}</div>
                <div className="text-xs text-[#4A5568] mt-1">
                  {r.payment_mode}{r.payment_ref ? ` · ${r.payment_ref}` : ""} · paid on {r.payment_date}
                </div>
                <div className="text-[11px] text-[#718096] mt-1">
                  {(r.lines && r.lines.length > 0
                    ? r.lines.filter((ln) => Number(ln.paid || 0) > 0)
                    : RECEIPT_KEYS.map((k) => ({ name: RECEIPT_LABELS[k].split(" ")[0], paid: r.components?.[k]?.paid || 0 })).filter((ln) => Number(ln.paid || 0) > 0)
                  ).map((ln, i) => `${(ln.name || "").split(" ")[0]} ${INR(ln.paid)}`).join(" · ") || "—"}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => downloadPdf(r.id, r.receipt_no)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0F1B47] px-3.5 py-2 rounded-full bg-[#E3EDF9] hover:bg-[#CFE0F0]"
                  data-testid={`receipt-download-${r.id}`}
                >
                  <Download size={12} /> PDF
                </button>
                {can("generate_receipts") && (
                  <button onClick={() => removeReceipt(r)} className="text-red-600 hover:text-red-700 p-2" title="Delete receipt">
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {showNew && (
        <NewReceiptModal
          student={student}
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); load(); }}
        />
      )}
      {showRaise && (
        <RaiseFeeModal
          student={student}
          onClose={() => setShowRaise(false)}
          onSaved={() => { setShowRaise(false); load(); }}
        />
      )}
    </Drawer>
  );
}

function NewReceiptModal({ student, onClose, onSaved }) {
  const [heads, setHeads] = useState([]);
  const [loadingHeads, setLoadingHeads] = useState(true);
  const [period, setPeriod] = useState("");
  const [paymentMode, setPaymentMode] = useState("Online");
  const [paymentRef, setPaymentRef] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  // rows: [{ row_id (local), head_id, amount }]
  const [rows, setRows] = useState([{ row_id: `r${Date.now()}`, head_id: "", amount: "" }]);

  const fee = student.fee_structure?.components || {};
  const slugPrefill = {
    application: Number(fee.application || 0),
    registration: Number(fee.registration || 0),
    security: Number(fee.security || 0),
    tuition: Number(fee.tuition || 0),
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/fee-heads", { params: { include_disabled: false } });
        if (cancelled) return;
        const active = (data || []).filter((h) => !h.disabled);
        setHeads(active);
      } catch (e) {
        toast.error("Could not load fee heads. Try Settings → Fee heads.");
      } finally {
        if (!cancelled) setLoadingHeads(false);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  const usedHeadIds = useMemo(() => new Set(rows.map((r) => r.head_id).filter(Boolean)), [rows]);

  const addRow = () => setRows((p) => [...p, { row_id: `r${Date.now()}${Math.random().toString(36).slice(2, 6)}`, head_id: "", amount: "" }]);
  const removeRow = (row_id) => setRows((p) => (p.length === 1 ? p : p.filter((r) => r.row_id !== row_id)));

  const pickHead = (row_id, head_id) => {
    setRows((p) => p.map((r) => {
      if (r.row_id !== row_id) return r;
      // Auto-fill amount from the student's existing fee_structure if this head is one of the defaults
      const head = heads.find((h) => h.id === head_id);
      const prefill = head?.is_default ? (slugPrefill[head.slug] || 0) : 0;
      const isEmpty = r.amount === "" || Number(r.amount) === 0;
      return {
        ...r,
        head_id,
        amount: isEmpty && prefill > 0 ? prefill : r.amount,
      };
    }));
  };

  const setAmount = (row_id, amount) => setRows((p) => p.map((r) =>
    r.row_id === row_id ? { ...r, amount: amount === "" ? "" : Number(amount) } : r
  ));

  const totalPaid = useMemo(() => rows.reduce((s, r) => s + Number(r.amount || 0), 0), [rows]);

  const submit = async (e) => {
    e.preventDefault();
    if (!period.trim()) return toast.error("Period is required (e.g. April 2026 or Term 1)");
    const valid = rows.filter((r) => r.head_id && Number(r.amount || 0) > 0);
    if (valid.length === 0) return toast.error("Add at least one fee head with an amount");
    const ids = valid.map((r) => r.head_id);
    if (new Set(ids).size !== ids.length) return toast.error("Each fee head can only appear once. Remove duplicates.");
    setSaving(true);
    try {
      const lines = valid.map((r) => {
        const head = heads.find((h) => h.id === r.head_id);
        const amt = Number(r.amount || 0);
        return {
          head_id: r.head_id,
          name: head?.name,
          due: amt,        // single-amount flow: Paid = Due, no concession
          concession: 0,
          paid: amt,
        };
      });
      const body = {
        period: period.trim(),
        lines,
        payment_mode: paymentMode,
        payment_ref: paymentRef.trim(),
        payment_date: paymentDate,
        note: note.trim(),
      };
      await adminApi.post(`/admin/students/${student.id}/receipts`, body);
      toast.success("Receipt generated. PDF is ready in the list.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F1B47]/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#FDFDF9] rounded-3xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="receipt-form">
        <div className="px-6 py-5 border-b border-[#0F1B47]/8 flex items-start justify-between gap-3 sticky top-0 bg-[#FDFDF9] z-10">
          <div>
            <h3 className="font-display text-xl text-[#0F1B47] font-medium">Generate fee receipt</h3>
            <p className="text-xs text-[#4A5568] mt-1">{student.full_name} · {student.admission_id}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-[#0F1B47]/5"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-5">
          <Grid>
            <Field label="Period *">
              <input value={period} onChange={(e) => setPeriod(e.target.value)} className={inputCls} placeholder="e.g. April 2026 or Term 1 · 2026-27" data-testid="receipt-period" />
            </Field>
            <Field label="Date of payment">
              <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} className={inputCls} data-testid="receipt-date" />
            </Field>
            <Field label="Payment mode">
              <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)} className={inputCls} data-testid="receipt-mode">
                {PAY_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="Reference / Transaction no.">
              <input value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} className={inputCls} placeholder="e.g. UPI ref / NEFT no." data-testid="receipt-ref" />
            </Field>
          </Grid>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[11px] font-bold tracking-[0.16em] uppercase text-[#648DE5]">Fee heads paid</div>
                <p className="text-xs text-[#4A5568] mt-1">
                  Pick a fee head, enter the amount — that amount becomes the paid value on the receipt.
                  Add more rows for additional heads. Manage the list under{" "}
                  <span className="text-[#0F1B47] font-medium">Settings → Fee heads</span>.
                </p>
              </div>
            </div>

            {loadingHeads ? (
              <div className="text-sm text-[#718096] py-6 text-center">Loading fee heads…</div>
            ) : heads.length === 0 ? (
              <div className="bg-[#F5F5F0] rounded-2xl p-6 text-center text-sm text-[#4A5568]">
                No active fee heads yet. Open <span className="font-semibold text-[#0F1B47]">Settings → Fee heads</span> to add one.
              </div>
            ) : (
              <div className="space-y-2" data-testid="receipt-rows">
                {rows.map((r, idx) => (
                  <div key={r.row_id} className="bg-[#F5F5F0] rounded-2xl p-3 flex items-center gap-3" data-testid={`receipt-row-${idx}`}>
                    <div className="flex-1 min-w-0">
                      <select
                        value={r.head_id}
                        onChange={(e) => pickHead(r.row_id, e.target.value)}
                        className="w-full bg-[#FDFDF9] border border-[#0F1B47]/12 rounded-xl px-3 py-2 text-[14px] text-[#0F1B47] focus:outline-none focus:border-[#648DE5]"
                        data-testid={`receipt-row-${idx}-head`}
                      >
                        <option value="">— Select fee head —</option>
                        {heads.map((h) => (
                          <option
                            key={h.id}
                            value={h.id}
                            disabled={h.id !== r.head_id && usedHeadIds.has(h.id)}
                          >
                            {h.name}{h.id !== r.head_id && usedHeadIds.has(h.id) ? " (already added)" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="w-40 shrink-0">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] font-medium text-[#718096]">₹</span>
                        <input
                          type="number" min="0" step="1"
                          value={r.amount}
                          onChange={(e) => setAmount(r.row_id, e.target.value)}
                          placeholder="0"
                          className="w-full bg-[#FDFDF9] border border-[#0F1B47]/12 rounded-xl pl-7 pr-3 py-2 text-right text-[14px] text-[#0F1B47] focus:outline-none focus:border-[#648DE5]"
                          data-testid={`receipt-row-${idx}-amount`}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRow(r.row_id)}
                      disabled={rows.length === 1}
                      className="p-2 rounded-full text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed shrink-0"
                      title="Remove row"
                      data-testid={`receipt-row-${idx}-remove`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addRow}
                  disabled={usedHeadIds.size >= heads.length}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#0F1B47]/5 text-[#0F1B47] rounded-2xl px-4 py-2.5 font-semibold text-sm hover:bg-[#0F1B47]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="receipt-row-add"
                >
                  + Add fee head
                </button>
                <div className="bg-[#0F1B47] text-[#FDFDF9] rounded-2xl px-4 py-3 flex items-center justify-between mt-3">
                  <span className="text-[11px] font-bold tracking-[0.16em] uppercase">Total paid</span>
                  <span className="text-base font-bold" data-testid="receipt-total-paid">{INR(totalPaid)}</span>
                </div>
              </div>
            )}
          </div>

          <Field label="Note (optional)">
            <input value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} placeholder="Internal note shown at the bottom of the receipt" />
          </Field>
        </div>

        <div className="px-6 py-4 border-t border-[#0F1B47]/8 flex items-center gap-3 sticky bottom-0 bg-[#FDFDF9]">
          <button type="submit" disabled={saving} className="bg-[#0F1B47] text-[#FDFDF9] rounded-full px-6 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b] disabled:opacity-50" data-testid="receipt-save">
            {saving ? "Saving…" : "Generate receipt"}
          </button>
          <button type="button" onClick={onClose} className="text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10">Cancel</button>
          <span className="ml-auto text-xs text-[#4A5568]">A "paid" entry is auto-added to the student's ledger.</span>
        </div>
      </form>
    </div>
  );
}

function RaiseFeeModal({ student, onClose, onSaved }) {
  const [heads, setHeads] = useState([]);
  const [loadingHeads, setLoadingHeads] = useState(true);
  const [period, setPeriod] = useState("");
  const [dueDate, setDueDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([{ row_id: `r${Date.now()}`, head_id: "", amount: "" }]);

  const fee = student.fee_structure?.components || {};
  const slugPrefill = {
    application: Number(fee.application || 0),
    registration: Number(fee.registration || 0),
    security: Number(fee.security || 0),
    tuition: Number(fee.tuition || 0),
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/fee-heads", { params: { include_disabled: false } });
        if (!cancelled) setHeads((data || []).filter((h) => !h.disabled));
      } catch (e) {
        toast.error("Could not load fee heads. Try Settings → Fee heads.");
      } finally {
        if (!cancelled) setLoadingHeads(false);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  const usedHeadIds = useMemo(() => new Set(rows.map((r) => r.head_id).filter(Boolean)), [rows]);
  const addRow = () => setRows((p) => [...p, { row_id: `r${Date.now()}${Math.random().toString(36).slice(2, 6)}`, head_id: "", amount: "" }]);
  const removeRow = (row_id) => setRows((p) => (p.length === 1 ? p : p.filter((r) => r.row_id !== row_id)));
  const pickHead = (row_id, head_id) => {
    setRows((p) => p.map((r) => {
      if (r.row_id !== row_id) return r;
      const head = heads.find((h) => h.id === head_id);
      const prefill = head?.is_default ? (slugPrefill[head.slug] || 0) : 0;
      const isEmpty = r.amount === "" || Number(r.amount) === 0;
      return { ...r, head_id, amount: isEmpty && prefill > 0 ? prefill : r.amount };
    }));
  };
  const setAmount = (row_id, amount) => setRows((p) => p.map((r) =>
    r.row_id === row_id ? { ...r, amount: amount === "" ? "" : Number(amount) } : r
  ));
  const totalDue = useMemo(() => rows.reduce((s, r) => s + Number(r.amount || 0), 0), [rows]);

  const submit = async (e) => {
    e.preventDefault();
    if (!period.trim()) return toast.error("Period is required (e.g. June 2026 or Term 1)");
    const valid = rows.filter((r) => r.head_id && Number(r.amount || 0) > 0);
    if (valid.length === 0) return toast.error("Add at least one fee head with an amount");
    const ids = valid.map((r) => r.head_id);
    if (new Set(ids).size !== ids.length) return toast.error("Each fee head can only appear once. Remove duplicates.");
    setSaving(true);
    try {
      await adminApi.post(`/admin/students/${student.id}/raise-fee`, {
        period: period.trim(),
        due_date: dueDate,
        note: note.trim(),
        lines: valid.map((r) => ({ head_id: r.head_id, amount: Number(r.amount) })),
      });
      toast.success(`Fee raised. The parent will see ${valid.length} pending invoice${valid.length === 1 ? "" : "s"} on their portal with a Pay button.`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to raise fee");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F1B47]/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#FDFDF9] rounded-3xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="raise-fee-form">
        <div className="px-6 py-5 border-b border-[#0F1B47]/8 flex items-start justify-between gap-3 sticky top-0 bg-[#FDFDF9] z-10">
          <div>
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#648DE5]">Send to parent</div>
            <h3 className="font-display text-xl text-[#0F1B47] font-medium mt-0.5">Raise fee</h3>
            <p className="text-xs text-[#4A5568] mt-1">{student.full_name} · {student.admission_id} — creates a pending invoice the parent can pay from their portal.</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-[#0F1B47]/5"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-5">
          <Grid>
            <Field label="Period *">
              <input value={period} onChange={(e) => setPeriod(e.target.value)} className={inputCls} placeholder="e.g. June 2026 or Term 1 · 2026-27" data-testid="raise-fee-period" />
            </Field>
            <Field label="Due date">
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls} data-testid="raise-fee-due-date" />
            </Field>
          </Grid>

          <div>
            <div className="text-[11px] font-bold tracking-[0.16em] uppercase text-[#648DE5]">Fee heads to charge</div>
            <p className="text-xs text-[#4A5568] mt-1 mb-3">Each row becomes one pending invoice on the parent's portal. The parent sees a Pay button per row.</p>

            {loadingHeads ? (
              <div className="text-sm text-[#718096] py-6 text-center">Loading fee heads…</div>
            ) : heads.length === 0 ? (
              <div className="bg-[#F5F5F0] rounded-2xl p-6 text-center text-sm text-[#4A5568]">
                No active fee heads yet. Open <span className="font-semibold text-[#0F1B47]">Settings → Fee heads</span> to add one.
              </div>
            ) : (
              <div className="space-y-2" data-testid="raise-fee-rows">
                {rows.map((r, idx) => (
                  <div key={r.row_id} className="bg-[#F5F5F0] rounded-2xl p-3 flex items-center gap-3" data-testid={`raise-fee-row-${idx}`}>
                    <div className="flex-1 min-w-0">
                      <select
                        value={r.head_id}
                        onChange={(e) => pickHead(r.row_id, e.target.value)}
                        className="w-full bg-[#FDFDF9] border border-[#0F1B47]/12 rounded-xl px-3 py-2 text-[14px] text-[#0F1B47] focus:outline-none focus:border-[#648DE5]"
                        data-testid={`raise-fee-row-${idx}-head`}
                      >
                        <option value="">— Select fee head —</option>
                        {heads.map((h) => (
                          <option
                            key={h.id}
                            value={h.id}
                            disabled={h.id !== r.head_id && usedHeadIds.has(h.id)}
                          >
                            {h.name}{h.id !== r.head_id && usedHeadIds.has(h.id) ? " (already added)" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="w-40 shrink-0">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] font-medium text-[#718096]">₹</span>
                        <input
                          type="number" min="0" step="1"
                          value={r.amount}
                          onChange={(e) => setAmount(r.row_id, e.target.value)}
                          placeholder="0"
                          className="w-full bg-[#FDFDF9] border border-[#0F1B47]/12 rounded-xl pl-7 pr-3 py-2 text-right text-[14px] text-[#0F1B47] focus:outline-none focus:border-[#648DE5]"
                          data-testid={`raise-fee-row-${idx}-amount`}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRow(r.row_id)}
                      disabled={rows.length === 1}
                      className="p-2 rounded-full text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
                      data-testid={`raise-fee-row-${idx}-remove`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addRow}
                  disabled={usedHeadIds.size >= heads.length}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#0F1B47]/5 text-[#0F1B47] rounded-2xl px-4 py-2.5 font-semibold text-sm hover:bg-[#0F1B47]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="raise-fee-row-add"
                >
                  + Add fee head
                </button>
                <div className="bg-[#0F1B47] text-[#FDFDF9] rounded-2xl px-4 py-3 flex items-center justify-between mt-3">
                  <span className="text-[11px] font-bold tracking-[0.16em] uppercase">Total to raise</span>
                  <span className="text-base font-bold" data-testid="raise-fee-total">{INR(totalDue)}</span>
                </div>
              </div>
            )}
          </div>

          <Field label="Note (optional)">
            <input value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} placeholder="Internal note appended to each invoice" />
          </Field>
        </div>

        <div className="px-6 py-4 border-t border-[#0F1B47]/8 flex items-center gap-3 sticky bottom-0 bg-[#FDFDF9]">
          <button type="submit" disabled={saving} className="bg-[#0F1B47] text-[#FDFDF9] rounded-full px-6 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b] disabled:opacity-50" data-testid="raise-fee-save">
            {saving ? "Sending…" : "Send to parent"}
          </button>
          <button type="button" onClick={onClose} className="text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10">Cancel</button>
          <span className="ml-auto text-xs text-[#4A5568]">Parent sees a Pay button per invoice on their portal.</span>
        </div>
      </form>
    </div>
  );
}


function PromoteStudentsModal({ fromSession, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await adminApi.get("/admin/students/promote/preview", { params: { from_session: fromSession } });
        if (cancelled) return;
        setPreview(data);
        setSelected(new Set((data.items || []).map((i) => i.id)));
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not load preview");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [fromSession]);

  const items = preview?.items || [];
  const grouped = useMemo(() => {
    const out = {};
    items.forEach((i) => {
      if (!out[i.class]) out[i.class] = [];
      out[i.class].push(i);
    });
    return out;
  }, [items]);
  const allSelected = items.length > 0 && selected.size === items.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((i) => i.id)));
  const toggleOne = (id) => setSelected((p) => {
    const next = new Set(p);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const willPromote = items.filter((i) => selected.has(i.id) && i.new_status === "active").length;
  const willPassOut = items.filter((i) => selected.has(i.id) && i.new_status === "passed_out").length;

  const apply = async () => {
    if (selected.size === 0) return toast.error("Pick at least one student");
    if (!window.confirm(`Promote ${selected.size} student${selected.size === 1 ? "" : "s"} from ${fromSession.replace("-", "–")} to ${preview.to_session.replace("-", "–")}?\n\n${willPromote} will move up a class.\n${willPassOut} (Early Years 3) will move to Archive as passed out.`)) return;
    setSaving(true);
    try {
      const { data } = await adminApi.post("/admin/students/promote", {
        from_session: fromSession,
        student_ids: Array.from(selected),
      });
      toast.success(`Promoted ${data.promoted}. Passed out ${data.passed_out}.`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Promotion failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F1B47]/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#FDFDF9] rounded-3xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="promote-students-modal">
        <div className="px-6 py-5 border-b border-[#0F1B47]/8 sticky top-0 bg-[#FDFDF9] z-10 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#648DE5]">Bulk action</div>
            <h3 className="font-display text-xl text-[#0F1B47] font-medium mt-0.5">Promote to next academic year</h3>
            <p className="text-xs text-[#4A5568] mt-1">
              {fromSession.replace("-", "–")} → <b className="text-[#0F1B47]">{(preview?.to_session || "").replace("-", "–")}</b>. EY3 students move to Archive as <i>Passed out</i>. Fee structures stay as-is.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-[#0F1B47]/5"><X size={16} /></button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-sm text-[#718096] py-8 text-center">Loading students…</div>
          ) : items.length === 0 ? (
            <div className="bg-[#F5F5F0] rounded-2xl p-8 text-center">
              <div className="text-[#0F1B47] font-medium">No active students in {fromSession.replace("-", "–")}</div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <label className="inline-flex items-center gap-2 text-[12px] font-semibold text-[#0F1B47] cursor-pointer" data-testid="promote-toggle-all">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 accent-[#0F1B47]" />
                  Select all ({items.length})
                </label>
                <span className="text-[11px] font-semibold text-[#4A5568]">
                  {selected.size} selected · {willPromote} promote · {willPassOut} pass out
                </span>
              </div>

              {Object.entries(grouped).map(([cls, list]) => (
                <div key={cls} className="mb-4">
                  <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#648DE5] mb-2">
                    {cls} → <span className="text-[#0F1B47]">{list[0].new_class || cls}</span>
                    {list[0].new_status === "passed_out" && (
                      <span className="ml-2 text-[9px] font-bold tracking-[0.16em] uppercase rounded-full px-2 py-0.5 bg-[#E3EDF9] text-[#0F1B47]">
                        Passed out
                      </span>
                    )}
                  </div>
                  <ul className="bg-[#F5F5F0] rounded-2xl divide-y divide-[#0F1B47]/8 overflow-hidden">
                    {list.map((it) => (
                      <li key={it.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`promote-row-${it.id}`}>
                        <input
                          type="checkbox"
                          checked={selected.has(it.id)}
                          onChange={() => toggleOne(it.id)}
                          className="w-4 h-4 accent-[#0F1B47] cursor-pointer"
                          data-testid={`promote-row-${it.id}-toggle`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold text-[#0F1B47]">{it.full_name}</div>
                          <div className="text-[10px] text-[#718096] mt-0.5">{it.admission_id}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#0F1B47]/8 sticky bottom-0 bg-[#FDFDF9] flex items-center gap-3">
          <button
            onClick={apply}
            disabled={saving || selected.size === 0}
            className="inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-6 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b] disabled:opacity-50"
            data-testid="promote-apply-btn"
          >
            <GraduationCap size={14} /> {saving ? "Promoting…" : `Promote ${selected.size} student${selected.size === 1 ? "" : "s"}`}
          </button>
          <button onClick={onClose} className="text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10">Cancel</button>
          <span className="ml-auto text-xs text-[#4A5568]">This action also updates each student's class.</span>
        </div>
      </div>
    </div>
  );
}

